import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import exExternal from '../../external';
import * as exButton from '../button';
import * as exColor from '../../utils/color';
import * as exElement from '../../utils/element';
import * as exEvent from '../../utils/event';

import type { IToastConfig } from './types';
import * as exUtils from './utils';

// ----------------------------------------------------------------------------

export type TypeBuildReturn = {
    element: HTMLElement;
    elements: {
        element: HTMLElement;
        toastHeader?: HTMLElement;
        toastBody?: HTMLElement;
        toastContainer?: HTMLElement;
        toastProgress?: HTMLElement;
    };
    bs?: any;
};

// ----------------------------------------------------------------------------

function create(config: IToastConfig = {}): TypeBuildReturn {
    // Default Config
    const defConfig: IToastConfig = {
        bootstrap: {
            enabled: true,
        },
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const build = {
        elements: {},
    } as TypeBuildReturn;

    // ------------------------------------------------------------------------

    // Create element (toast)
    const elToast = exElement.create({
        merge: defConfig,
        class: {
            new: 'toast',
        },
        attr: {
            role: 'alert',
        },
    });
    build.elements.element = elToast;
    build.element = elToast;

    // ------------------------------------------------------------------------

    // If using toast header
    if (defConfig.title != null || !isEmptyObject(defConfig.toastHeader)) {
        // Create element (toast header)
        const elToastHeader = exElement.create({
            merge: defConfig.toastHeader,
            class: {
                new: 'toast-header',
            },
        });
        elToast.appendChild(elToastHeader);
        build.elements.toastHeader = elToastHeader;
    }

    // ------------------------------------------------------------------------

    // Create element (toast body)
    const elToastBody = exElement.create({
        merge: defConfig.toastBody,
        class: {
            new: 'toast-body',
        },
        html: defConfig.message,
    });
    elToast.appendChild(elToastBody);
    build.elements.toastBody = elToastBody;

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig);

    // Events
    events(build, defConfig);

    // Methods
    methods(build, defConfig);

    // ------------------------------------------------------------------------

    // If using bootstrap
    if (defConfig.bootstrap?.enabled === true) {
        // Bootstrap (toast)
        const bsToast = new exExternal.bootstrap.Toast(build.element, defConfig.bootstrap);
        build.bs = bsToast;
    }

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function settings(build: TypeBuildReturn, config: IToastConfig = {}): void {
    // If using title
    if (config.title != null) {
        // If title is string
        if (typeof config.title === 'string') {
            config.title = {
                html: config.title,
            };
        }

        // Create element (title)
        const elTitle = exElement.create({
            merge: config.title,
            tag: 'strong',
            class: {
                add: 'me-auto',
            },
        });
        build.elements.toastHeader?.appendChild(elTitle);
    }

    // If using type
    if (config.type != null) {
        // Get color type
        const color = exColor.type(config.type, {
            first: 'toast-',
        });
        addClass(build.element, color);
    }

    // ------------------------------------------------------------------------

    // If using allow dismiss
    if (config.allowDismiss === true) {
        // Create element (button close)
        const buttonClose = exButton.create({
            class: {
                new: 'btn-close',
            },
            dataset: {
                bsDismiss: 'toast',
            },
        });

        //
        if (build.elements.toastHeader) {
            build.elements.toastHeader.appendChild(buttonClose.element);
        }
        //
        else {
            addClass(build.element, 'toast-dismissible');
            build.element.appendChild(buttonClose.element);
        }
    }

    // ------------------------------------------------------------------------

    // If using progress
    if (config.progress === true) {
        // Create element (toast progress)
        const elToastProgress = exElement.create({
            merge: config.toastProgress,
            class: {
                new: 'toast-progress',
            },
        });
        build.element.appendChild(elToastProgress);
        build.elements.toastProgress = elToastProgress;
    }

    // ------------------------------------------------------------------------

    // If using toast container
    if (config.toastContainer === true || !isEmptyObject(config.toastContainer)) {
        // Create element (toast container)
        const elToastContainer = exUtils.alreadyToastContainer(build.element, config.toastContainer as any);
        build.elements.toastContainer = elToastContainer;
    }
}

function events(build: TypeBuildReturn, config: IToastConfig = {}): void {
    // Create events (show toast)
    build.element.addEventListener('show.bs.toast', function (this: HTMLElement, evt): any {
        // If there is show
        if (this.hasClass('show')) {
            evt.preventDefault();

            // Reset
            exUtils.resetTimeout(this);
        }
    });

    // ------------------------------------------------------------------------

    // If using toast event
    if (config.toastEvent && !isEmptyObject(config.toastEvent)) {
        for (const key of Object.keys(config.toastEvent)) {
            const value = config.toastEvent[key];

            const type = key + '.bs.toast';

            // Create event
            exEvent.create(build.element, {
                [type]: value,
            });
        }
    }

    // ------------------------------------------------------------------------

    // If using destroy
    if (config.destroy === true) {
        // Show toast and add handler that will remove it from DOM once closed
        build.element.addEventListener('hidden.bs.toast', function (this: any): any {
            (build.elements.toastContainer ?? build.element).remove();
        });
    }
}

function methods(build: TypeBuildReturn, config: IToastConfig = {}): void {
    // Do nothing
}

// ----------------------------------------------------------------------------

export { create };
